import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { HomeComponent } from './front/home/home.component';
import { LoggedInGuard } from './guards/logged-in.guard';
import { ResetPasswordComponent } from './front/reset-password/reset-password.component';
import { ConfirmedAccountGuard } from './guards/confirmed-account.guard';
import { LoginComponent } from './front/login/login.component';
import { RegisterComponent } from './front/register/register.component';
import { ForgotPasswordComponent } from './front/forgot-password/forgot-password.component';
import { BrokerComponent } from './front/broker/broker.component';
import { ExchangeComponent } from './front/exchange/exchange.component';
import { FeesPageComponent } from './front/fees/fees-page/fees-page.component';
import { LogoutComponent } from './front/logout/logout.component';
import { LoggedOutGuard } from './guards/logged-out.guard';
import { SupportPageComponent } from './front/support-page/support-page.component';
import { PageComponent } from './front/page/page.component';
import { PageNotFoundComponent } from './front/page-not-found/page-not-found.component';
import { EmailsUnsubscribeComponent } from './front/emails-unsubscribe/emails-unsubscribe.component';
import { VerifyIpAddressComponent } from './front/verify-ip-address/verify-ip-address.component';
import { HasWalletGuard } from './guards/has-wallet.guard';
import { NoWalletComponent } from './front/no-wallet/no-wallet.component';
import { PricesPageComponent } from './front/prices-page/prices-page.component';
import { CryptoPageComponent } from './front/crypto-page/crypto-page.component';
import { GeneralLayoutComponent } from './layout/general-layout/general-layout.component';
import { AboutUsComponent } from './front/about-us/about-us.component';
import { OtcPageComponent } from './front/otc-page/otc-page.component';
import { LoggedInCookieGuard } from './guards/logged-in-cookie-guard';
import { SupportedTokensComponent } from './front/supported-tokens/supported-tokens.component';
import { TradingChartPageComponent } from './front/trading-chart-page/trading-chart-page.component';
import { VipPageComponent } from './front/vip-page/vip-page.component';

const routes: Routes = [
  { path: '', redirectTo: 'home', pathMatch: 'full' },
  { path: 'home', component: HomeComponent },
  { path: 'login', canActivate: [LoggedOutGuard], component: LoginComponent },
  { path: 'logout', canActivate: [LoggedInGuard], component: LogoutComponent },
  { path: 'register', canActivate: [LoggedOutGuard], component: RegisterComponent },
  { path: 'verify-ip-address', canActivate: [LoggedOutGuard], component: VerifyIpAddressComponent },
  { path: 'forgot-password', component: ForgotPasswordComponent },
  { path: 'reset-password', component: ResetPasswordComponent },
  { path: 'email-unsubscribe', component: EmailsUnsubscribeComponent },
  { path: 'prices', component: PricesPageComponent },
  { path: 'prices/:pair', component: CryptoPageComponent },
  { path: 'about-us', component: AboutUsComponent },

  {
    path: 'setup',
    canActivate: [LoggedInGuard],
    loadChildren: () => import('./account-setup/account-setup.module').then(m => m.AccountSetupModule)
  },
  {
    path: '',
    component: GeneralLayoutComponent,
    canActivate: [LoggedInGuard, ConfirmedAccountGuard, HasWalletGuard],
    children: [
      {
        path: 'dashboard',
        loadChildren: () => import('./dashboard/dashboard.module').then(m => m.DashboardModule),
        data: { oldLayout: true },
      },
      {
        path: 'accounts',
        loadChildren: () => import('./accounts/accounts.module').then(m => m.AccountsModule)
      },
      {
        path: 'lite',
        loadChildren: () => import('./core-exchange/core-exchange.module').then(m => m.CoreExchangeModule),
      },
      // {
      //   path: 'core-old',
      //   loadChildren: () => import('./wallet-exchange/wallet-exchange.module').then(m => m.WalletExchangeModule)
      // },
      {
        path: 'verification',
        loadChildren: () => import('./verification/verification.module').then(m => m.VerificationModule)
      },
      {
        path: 'affiliate-program',
        loadChildren: () => import('./affiliate-program/affiliate-program.module').then(m => m.AffiliateProgramModule)
      },
      {
        path: 'settings',
        canActivate: [LoggedInGuard, ConfirmedAccountGuard, HasWalletGuard],
        loadChildren: () => import('./settings/settings.module').then(m => m.SettingsModule)
      },
      // Pro
      {
        path: 'pro',
        canActivate: [LoggedInCookieGuard, LoggedInGuard, ConfirmedAccountGuard, HasWalletGuard],
        loadChildren: () => import('./exchange/exchange.module').then(m => m.ExchangeModule),
        data: { minifySidebar: true, oldLayout: true },
      },
      {
        path: 'pro/pnl',
        canActivate: [LoggedInCookieGuard, LoggedInGuard, ConfirmedAccountGuard, HasWalletGuard],
        loadChildren: () =>  import('./pnl/pnl.module').then(m => m.PnlModule),
        data: { minifySidebar: true, oldLayout: true },
      },
    ]
  },

  // Pro chart for mobile
  // TODO: pro/full-chart is kept for backward compatibility with mobile. Remove once old app versions are dupricated
  {
    path: 'pro/full-chart',
    component: TradingChartPageComponent
  }, // remove this
  { path: 'trading-chart', component: TradingChartPageComponent}, // Keep this
  {
    path: 'no-wallet',
    canActivate: [LoggedInGuard, ConfirmedAccountGuard],
    component: NoWalletComponent
  },
  {
    path: 'support',
    children: [
      { path: '',  component: SupportPageComponent},
      { path: ':type',  component: SupportPageComponent}
    ]
  },
  // { path: 'invite-and-earn', component: AffiliateProgramComponent},
  // { path: 'trading-competition', component: TradingCompetitionComponent},
  // { path: 'invite-and-win-competition', component: InviteAndEarnCompetitionComponent},
  { path: 'otc', component: OtcPageComponent},
  { path: 'page',
    children: [
      { path: 'core', redirectTo: 'lite', pathMatch: 'full' },
      { path: 'lite',  component: BrokerComponent}
    ]
  },
  { path: 'page/pro', component: ExchangeComponent},
  { path: 'page/fees',
    canActivate: [LoggedInCookieGuard],
    children: [
      { path: '', redirectTo: 'fiat', pathMatch: 'full' },
      { path: ':tab',  component: FeesPageComponent}
    ]
  },
  { path: 'page/vip', canActivate: [LoggedInCookieGuard],component: VipPageComponent },
  { path: 'page/supported-tokens', component: SupportedTokensComponent},
  { path: 'page/:key', component: PageComponent},

  // Resturcture old links
  // -------------------------

  // old pro fees
  { path: 'pro/fees', redirectTo: '/page/fees/fiat', pathMatch: 'full' },

  { path: 'core', redirectTo: 'lite', pathMatch: 'full' },
  { path: 'core/exchange/:mode', redirectTo: 'lite/exchange/:mode', pathMatch: 'full' },
  { path: 'core/exchange/details/:id', redirectTo: 'lite/exchange/details/:id', pathMatch: 'full' },

  { path: 'core/dashboard', redirectTo: '/dashboard', pathMatch: 'full' },
  { path: 'core/accounts', redirectTo: '/accounts', pathMatch: 'full' },

  // Fiat Deposit
  { path: 'core/deposit', redirectTo: '/accounts/fiat/deposit', pathMatch: 'full' },
  { path: 'core/deposit/:id', redirectTo: '/accounts/fiat/deposit/:id', pathMatch: 'full' },
  { path: 'core/deposit/history', redirectTo: '/accounts/fiat/deposit/history', pathMatch: 'full' },

  // Fiat Withdrawal
  { path: 'core/withdraw', redirectTo: '/accounts/fiat/withdraw', pathMatch: 'full' },
  { path: 'core/withdraw/:id', redirectTo: '/accounts/fiat/withdraw/:id', pathMatch: 'full' },
  { path: 'core/withdraw/history', redirectTo: '/accounts/fiat/withdraw/history', pathMatch: 'full' },

  // Multisig
  { path: 'core/transaction/send', redirectTo: '/accounts/wallet/send', pathMatch: 'full' },
  { path: 'core/transaction/send/:id', redirectTo: '/accounts/wallet/send/:id', pathMatch: 'full' },
  { path: 'core/transaction/send/confirm/:id', redirectTo: '/accounts/wallet/send/confirm/:id', pathMatch: 'full' },
  { path: 'core/transaction/receive', redirectTo: '/accounts/wallet/receive', pathMatch: 'full' },
  { path: 'core/transaction/addresses', redirectTo: '/accounts/wallet/addresses', pathMatch: 'full' },
  { path: 'core/transaction/history', redirectTo: '/accounts/wallet/history', pathMatch: 'full' },
  { path: 'core/transaction/details/:id', redirectTo: '/accounts/wallet/details/:id', pathMatch: 'full' },

  // Crypto Deposit
  { path: 'core/crypto/deposit', redirectTo: '/accounts', pathMatch: 'full' },
  { path: 'core/crypto/deposit/:currency', redirectTo: '/accounts/crypto/deposit/:currency', pathMatch: 'full' },
  { path: 'core/crypto/deposit/history/:currency', redirectTo: '/accounts/crypto/deposit/history/:currency', pathMatch: 'full' },
  { path: 'core/crypto/deposit/details/:id', redirectTo: '/accounts/crypto/deposit/details/:id', pathMatch: 'full' },

  // Crypto Withdrawal
  { path: 'core/crypto/withdraw', redirectTo: '/accounts', pathMatch: 'full' },
  { path: 'core/crypto/withdraw/:currency', redirectTo: '/accounts/crypto/withdraw/:currency', pathMatch: 'full' },
  { path: 'core/crypto/withdraw/history/:currency', redirectTo: '/accounts/crypto/withdraw/history/:currency', pathMatch: 'full' },
  { path: 'core/crypto/withdraw/details/:id', redirectTo: '/accounts/crypto/withdraw/details/:id', pathMatch: 'full' },
  { path: 'core/crypto/withdraw/confirm/:id', redirectTo: '/accounts/crypto/withdraw/confirm/:id', pathMatch: 'full' },


  // Verification
  { path: 'core/verify', redirectTo: '/verification', pathMatch: 'full' },
  { path: 'core/verify/tier-one', redirectTo: '/verification/tier-one', pathMatch: 'full' },
  { path: 'core/verify/tier-one/additional-documents', redirectTo: '/verification/tier-one/additional-documents', pathMatch: 'full' },
  { path: 'core/verify/tier-two', redirectTo: '/verification/tier-two', pathMatch: 'full' },
  { path: 'core/verify/tier-two/additional-documents', redirectTo: '/verification/tier-two/additional-documents', pathMatch: 'full' },
  { path: 'core/verify/tier-three', redirectTo: '/verification/tier-three', pathMatch: 'full' },
  { path: 'core/verify/tier-three/additional-documents', redirectTo: '/verification/tier-three/additional-documents', pathMatch: 'full' },

  // Fix inviate & earn learn more link on mobile
  { path: 'invite-and-earn-competition',  redirectTo: 'page/invite-earn-program-guidelines'},

  // -------------------------
  // End of resturcture old links

  // Old URLs migration
  { path: 'front', children: [
    { path: 'contact',  redirectTo: '/support'},
    { path: 'sign-in',  redirectTo: '/login'},
    { path: 'sign-up',  redirectTo: '/register'},
    { path: 'forgotten-password',  redirectTo: '/forgot-password'},
    { path: ':name',  redirectTo: '/page/:name'},
    { path: '',  redirectTo: '/home', pathMatch: 'full'}
  ]},

  // error routes
  { path: '**', component: PageNotFoundComponent }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { relativeLinkResolution: 'legacy' })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
